// Synthesized metronome click voices. Everything is generated with oscillators
// and a shared noise buffer, so there are no audio files to host, preload or
// keep in sync with the CDN — the click is available the instant the page is.

import type { TickLevel } from './metronomeMeter'

export type VoiceId = 'click' | 'ping' | 'stick' | 'woodblock' | 'drum'

export interface VoiceRenderOptions {
  /** User pitch trim in semitones, -12..+12. */
  pitch?: number
}

export interface VoiceDefinition {
  id: VoiceId
  /** i18n key under `metronome.voices` — labels are translated, not hardcoded. */
  labelKey: string
  render: (
    ctx: AudioContext,
    destination: AudioNode,
    time: number,
    level: TickLevel,
    options?: VoiceRenderOptions
  ) => void
}

/** Relative loudness per tier. Subdivisions sit well under the pulse. */
const LEVEL_GAIN: Record<TickLevel, number> = {
  accent: 1,
  beat: 0.62,
  sub: 0.3,
}

/**
 * Relative pitch per tier. Accents sit above the beat and subdivisions above
 * both, which is what makes a bar readable by ear without looking at the dots.
 */
const LEVEL_PITCH: Record<TickLevel, number> = {
  accent: 1.25,
  beat: 1,
  sub: 1.5,
}

// One noise buffer per context, built lazily. A WeakMap so a closed context
// (the dock is destroyed on close) doesn't pin its buffer in memory.
const noiseBuffers = new WeakMap<AudioContext, AudioBuffer>()

function getNoiseBuffer(ctx: AudioContext): AudioBuffer {
  const cached = noiseBuffers.get(ctx)
  if (cached) return cached

  const length = Math.floor(ctx.sampleRate * 0.2)
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < length; i++) {
    data[i] = Math.random() * 2 - 1
  }

  noiseBuffers.set(ctx, buffer)
  return buffer
}

function pitchRatio(level: TickLevel, options?: VoiceRenderOptions): number {
  const semitones = options?.pitch ?? 0
  return LEVEL_PITCH[level] * Math.pow(2, semitones / 12)
}

/**
 * A percussive gain envelope. exponentialRampToValueAtTime can never reach 0,
 * so we ramp to a floor and then hard-stop the node.
 */
function envelope(ctx: AudioContext, time: number, peak: number, decay: number): GainNode {
  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0.0001, time)
  gain.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), time + 0.001)
  gain.gain.exponentialRampToValueAtTime(0.0001, time + decay)
  return gain
}

function tone(
  ctx: AudioContext,
  destination: AudioNode,
  time: number,
  type: OscillatorType,
  frequency: number,
  peak: number,
  decay: number
): OscillatorNode {
  const osc = ctx.createOscillator()
  const gain = envelope(ctx, time, peak, decay)

  osc.type = type
  osc.frequency.setValueAtTime(frequency, time)
  osc.connect(gain)
  gain.connect(destination)
  osc.start(time)
  osc.stop(time + decay + 0.02)

  return osc
}

function noise(
  ctx: AudioContext,
  destination: AudioNode,
  time: number,
  filter: BiquadFilterNode,
  peak: number,
  decay: number
): void {
  const source = ctx.createBufferSource()
  const gain = envelope(ctx, time, peak, decay)

  source.buffer = getNoiseBuffer(ctx)
  source.connect(filter)
  filter.connect(gain)
  gain.connect(destination)
  source.start(time)
  source.stop(time + decay + 0.02)
}

export const VOICES: VoiceDefinition[] = [
  {
    // The original click, kept as the default so existing users hear no change.
    id: 'click',
    labelKey: 'click',
    render: (ctx, dest, time, level, options) => {
      const ratio = pitchRatio(level, options)
      tone(ctx, dest, time, 'triangle', 800 * ratio, LEVEL_GAIN[level], 0.025)
    },
  },
  {
    id: 'ping',
    labelKey: 'ping',
    render: (ctx, dest, time, level, options) => {
      const ratio = pitchRatio(level, options)
      tone(ctx, dest, time, 'sine', 1400 * ratio, LEVEL_GAIN[level] * 0.9, 0.12)
    },
  },
  {
    id: 'stick',
    labelKey: 'stick',
    render: (ctx, dest, time, level, options) => {
      const ratio = pitchRatio(level, options)
      const filter = ctx.createBiquadFilter()
      filter.type = 'highpass'
      filter.frequency.setValueAtTime(1800 * ratio, time)
      noise(ctx, dest, time, filter, LEVEL_GAIN[level], 0.014)
      tone(ctx, dest, time, 'square', 2400 * ratio, LEVEL_GAIN[level] * 0.18, 0.01)
    },
  },
  {
    id: 'woodblock',
    labelKey: 'woodblock',
    render: (ctx, dest, time, level, options) => {
      const ratio = pitchRatio(level, options)
      const filter = ctx.createBiquadFilter()
      filter.type = 'bandpass'
      filter.frequency.setValueAtTime(1150 * ratio, time)
      filter.Q.setValueAtTime(12, time)
      noise(ctx, dest, time, filter, LEVEL_GAIN[level] * 1.4, 0.05)
      tone(ctx, dest, time, 'square', 1150 * ratio, LEVEL_GAIN[level] * 0.3, 0.045)
    },
  },
  {
    id: 'drum',
    labelKey: 'drum',
    render: (ctx, dest, time, level, options) => {
      const ratio = pitchRatio(level, options)
      const osc = ctx.createOscillator()
      const gain = envelope(ctx, time, LEVEL_GAIN[level], 0.16)

      // Pitch drop is what reads as a drum rather than a bass note.
      osc.type = 'sine'
      osc.frequency.setValueAtTime(190 * ratio, time)
      osc.frequency.exponentialRampToValueAtTime(58 * ratio, time + 0.14)
      osc.connect(gain)
      gain.connect(dest)
      osc.start(time)
      osc.stop(time + 0.2)

      const filter = ctx.createBiquadFilter()
      filter.type = 'highpass'
      filter.frequency.setValueAtTime(2200, time)
      noise(ctx, dest, time, filter, LEVEL_GAIN[level] * 0.35, 0.012)
    },
  },
]

export const DEFAULT_VOICE: VoiceId = 'click'

export function getVoice(id: VoiceId | string): VoiceDefinition {
  return VOICES.find((voice) => voice.id === id) ?? VOICES[0]!
}
